import type { VehicleDetails } from './vehicleDetails';
import type { UserDetails } from './userDetails';
import { FileObject } from './shared/file';

// Service and Damage interfaces for images
export interface ServiceDetail {
  id: string;
  date: Date;
  mileage: number;
  serviceType: string;
  notes: string;
}

// Image interfaces with additional properties
export interface ExteriorImage extends FileObject {
    isThumbnail?: boolean;
    /** If set, this is a 360-view frame – exclude from gallery/PDF/document/post-ad */
    frameIndex?: number | null;
}

/** Returns true if image is a 360 frame (has frameIndex or frame_index). Exclude from gallery/PDF/document/post-ad. */
export function is360ExteriorFrame(img: unknown): boolean {
  if (!img || typeof img !== "object") return false;
  const o = img as { frameIndex?: number | null; frame_index?: number };
  const fi = o.frameIndex ?? o.frame_index;
  return fi != null;
}

/** 360 exterior frame – separate list from exterior, excluded from gallery/PDF/document view */
export interface Exterior360Frame extends FileObject {
    frameIndex: number;
}

export interface DamageImage extends FileObject {
    comment?: string;
}

export interface ServiceDetails {
    serviceDetails: ServiceDetail[];
}

// Main VehicleTestImages interface
export interface VehicleTestImages {
  exterior?: ExteriorImage[];
  /** 360 exterior frames – separate list, excluded from exterior */
  exterior360?: Exterior360Frame[];
  interior?: FileObject[];
  damage?: DamageImage[];
  serviceImages?: FileObject[]; // Simple FileObject array since no additional properties needed
  serviceDetails?: ServiceDetails;
  tires?: {
    primary?: FileObject[];
    secondary?: FileObject[];
  };
}

// Image files structure for createTest - matches VehicleTestImages but with Files instead of FileObjects. This is used to upload images to the backend. Whule VehicleTestImages is used to display images in the UI.
export interface VehicleTestImageFiles {
  exterior?: { file: File; isThumbnail?: boolean }[];
  interior?: { file: File }[];
  damage?: { file: File; comment?: string }[];
  serviceImages?: { file: File }[];
  serviceDetails?: ServiceDetails;
  tires?: {
    primary?: { file: File }[];
    secondary?: { file: File }[];
  };
}

export interface CreateTestRequest {
    vehicleName: string;
    vehicleDetails: Partial<VehicleDetails>;
    vehicleTestData: VehicleTestData;
    imageFiles?: VehicleTestImageFiles; // Optional - images are now uploaded via lazy upload after test creation
    price: string;
    comments?: string;
    vatCar: boolean;
    customerId?: string;
    isDraft: boolean;
}

export interface CreateTestResponse {
    id: string;
    status: 'pending' | 'approved' | 'rejected';
    message: string;
    createdAt: string;
}

export interface VehicleTest {
    id: string;
    vehicleName: string;
    regNumber?: string;
    /** VIN can come from vehicleDetails or top-level (CarTest.vin). */
    vin?: string | null;
    date: string;
    vehicleDetails: VehicleDetails;
    vehicleTestData: VehicleTestData;
    images: VehicleTestImages;
    price: string;
    comments?: string;
    vatCar: boolean;
    customerId?: string;
    customer?: UserDetails;
    status: 'ongoing' | 'approved' | 'rejected' | 'complement' | 'pending-review' | 'change-requested' | 'created' | 'completed' | 'active-listing';
    changeRequestMessage?: string | null;
    /** Admin's message when status is complement (what the customer needs to complement). */
    reviewNotes?: string | null;
    isArchived?: boolean;
    purchasePrice?: number;
    createdAt: string;
    updatedAt: string;
}

export interface VehicleTestData {
    speed?: number;
    vibrations?: { problem?: "yes" | "no"; location?: string; other?: string };
    leaks?: { problem?: "yes" | "no"; source?: string; other?: string };
    noises?: { problem?: "yes" | "no"; type?: string; other?: string };
    gearbox?: { problem?: "yes" | "no"; issue?: string; other?: string };
    crash?: { problem?: "yes" | "no" | "unknown"; area?: string; other?: string };
    ac?: { problem?: "yes" | "no" | "not_tested"; issue?: string; other?: string };
    dashboard?: { problem?: "yes" | "no"; issue?: string; other?: string };
    windshield?: { problem?: "yes" | "no" | "stone_chip"; description?: string };
    brakes?: {
        frontEffectiveness?: "10-30%" | "40-60%" | "70-100%";
        rearEffectiveness?: "10-30%" | "40-60%" | "70-100%";
    };
    // Tire structure with primary and secondary sets
    tires?: {
      primary: {
        frontDepth?: "<2mm" | "3-4mm" | "5-6mm" | "7-8mm";
        rearDepth?: "<2mm" | "3-4mm" | "5-6mm" | "7-8mm";
      };
      secondary?: {
        frontDepth?: "<2mm" | "3-4mm" | "5-6mm" | "7-8mm";
        rearDepth?: "<2mm" | "3-4mm" | "5-6mm" | "7-8mm";
      };
    };
  }

export interface GetAllTestsResponse {
    tests: VehicleTest[];
    total: number;
    page: number;
    limit: number;
}

export interface UpdateTestStatusRequest {
    status?: 'pending' | 'approved' | 'rejected' | 'complement'; // TODO: pending not needed? if it is, should not be pending-review?
    notes?: string;
    rejectionReason?: string; // Reason for rejection (required when status is 'rejected')
}

export interface UpdateVehicleTestImagesRequest {
    testId: string;
    addImages: {
        file: File;
        section: 'exterior' | 'interior' | 'damage' | 'serviceImages' | 'tires';
        isThumbnail?: boolean; // For exterior images that are thumbnails
        tireType?: 'primary' | 'secondary'; // For tires section
        comment?: string; // For damage images
    }[];
    removeImages: string[]; // Array of image IDs to remove
    // Service details changes
    addServiceDetails?: ServiceDetail[];
    removeServiceDetails?: string[]; // Array of service detail IDs to remove
    // Damage image comment changes
    updateDamageComments?: {
        imageId: string;
        comment: string;
    }[];
}

export interface UpdateTestRequest {
    vehicleName: string;
    vehicleDetails: Partial<VehicleDetails>;
    vehicleTestData: VehicleTestData;
    images?: VehicleTestImages; // Optional - images are handled via lazy upload pattern (separate endpoint)
    price: string;
    comments?: string;
    vatCar: boolean;
    customerId?: string;
}

export interface UpdateTestResponse {
    id: string;
    status: 'pending' | 'approved' | 'rejected';
    message: string;
    updatedAt: string;
}
